import { createTreeCollection, TreeView, useTreeView } from "@ark-ui/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { twJoin } from "tailwind-merge";

import type {
  TicketCategoryTreeDto,
  TicketCategoryTreeNodeDto,
  TicketCategoryTreeNodeUpdateRequest,
  TicketCategoryTreeSettingsDto,
} from "@/api/types";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { Select } from "@/components/Select/Select";
import {
  useDisableTicketCategoryNode,
  useEditTicketCategoryNode,
  useEnableTicketCategoryNode,
  useUpdateTicketCategorySettings,
} from "@/queries/ticket-categories/mutations";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { useTranslation } from "@/translations";

import { searchTree } from "../helpers/tree-helpers";

type LocationType = TicketCategoryTreeNodeUpdateRequest["locationType"];

export function TicketCategoryTree(): React.ReactNode {
  const queryOptions = useTicketCategoryQueries();
  const { data: treeSettings } = useSuspenseQuery(queryOptions.ticketCategoryTreeSettings());
  const { data: tree } = useSuspenseQuery(queryOptions.ticketCategoryTree());

  const editTicketCategorySettingsMutation = useUpdateTicketCategorySettings();
  const editTicketCategoryNodeMutation = useEditTicketCategoryNode();
  const enableTicketCategoryNodeMutation = useEnableTicketCategoryNode();
  const disableTicketCategoryNodeMutation = useDisableTicketCategoryNode();

  return (
    <TreeContent
      tree={tree}
      settings={treeSettings}
      onEditSettings={(data) => editTicketCategorySettingsMutation.mutate(data)}
      onEditNode={(data) => editTicketCategoryNodeMutation.mutate(data)}
      onEnableNode={enableTicketCategoryNodeMutation.mutate}
      onDisableNode={disableTicketCategoryNodeMutation.mutate}
    />
  );
}

function TreeContent({
  tree,
  settings,
  onEditSettings,
  onEditNode,
  onEnableNode,
  onDisableNode,
}: {
  tree: TicketCategoryTreeDto;
  settings: TicketCategoryTreeSettingsDto;
  onEditSettings: (data: { vveEnabled: boolean }) => void;
  onEditNode: (data: { node: TicketCategoryTreeNodeDto; locationType: LocationType }) => void;
  onEnableNode: (node: TicketCategoryTreeNodeDto) => void;
  onDisableNode: (node: TicketCategoryTreeNodeDto) => void;
}) {
  const { t } = useTranslation();
  const collection = useMemo(() => {
    const root = {
      id: "root",
      name: "",
      children: tree.nodes,
      enabled: true,
      isFinal: false,
      locationType: "insideOrOutside",
    } satisfies TicketCategoryTreeNodeDto;

    const filteredTree = searchTree(root, "");

    return createTreeCollection<TicketCategoryTreeNodeDto>({
      nodeToValue: (node) => node.id,
      nodeToString: (node) => node.name,
      rootNode: filteredTree || {
        id: "root",
        name: "No results",
        children: [],
        enabled: true,
        isFinal: false,
        locationType: "insideOrOutside",
      },
    });
  }, [tree]);

  const treeView = useTreeView({
    defaultExpandedValue: [collection.rootNode.id],
    collection,
    typeahead: false,
  });

  return (
    <div className="flex flex-col gap-8 rounded-lg bg-white p-4">
      <div>
        <h3 className="mb-1 text-headline3">{t("page.ticket-category-tree.title")}</h3>
        <Checkbox
          checked={settings.vveEnabled}
          onChange={(e) => onEditSettings({ vveEnabled: e.target.checked })}
          label={t("page.ticket-category-tree.enable-vve-features.title")}
        />
        <p className="text-grey-700">{t("page.ticket-category-tree.enable-vve-features.description")}</p>
      </div>
      <TreeView.RootProvider value={treeView} className="max-w-4xl">
        <TreeView.Label className="flex flex-col gap-1">
          <h4 className="text-headline4">{t("page.ticket-category-tree.tree.title")}</h4>
        </TreeView.Label>
        <TreeView.Tree>
          {collection.rootNode.children?.map((node, index) => (
            <TreeNode
              key={node.id}
              node={node}
              indexPath={[index]}
              depth={0}
              onEdit={onEditNode}
              onToggle={(subNode) => {
                if (treeView.expandedValue.includes(subNode.id)) {
                  // Unfortunately we need to include this logic because otherwise the tree collapses upon clicking the checkbox.
                  // This has the side effect of opening the tree if it was closed, but we can live with that.
                  treeView.expand([subNode.id]);
                }

                if (subNode.enabled) {
                  onDisableNode(subNode);
                } else {
                  onEnableNode(subNode);
                }
              }}
            />
          ))}
        </TreeView.Tree>
      </TreeView.RootProvider>
    </div>
  );
}

function TreeNode({
  node,
  indexPath,
  depth,
  isLastChild,
  onEdit,
  onToggle,
}: TreeView.NodeProviderProps<TicketCategoryTreeNodeDto> & {
  depth: number;
  isLastChild?: boolean;
  onEdit: (data: { node: TicketCategoryTreeNodeDto; locationType: LocationType }) => void;
  onToggle: (node: TicketCategoryTreeNodeDto) => void;
}) {
  const { t } = useTranslation();

  return (
    <TreeView.NodeProvider key={node.id} node={node} indexPath={indexPath}>
      {node.children && node.children.length > 0 && (
        <TreeView.Branch className="relative">
          <TreeView.BranchControl className="flex items-center py-3">
            {!node.isFinal && (
              <TreeView.BranchIndicator className="flex items-center gap-2 *:data-[state=open]:rotate-90">
                {depth > 0 && <BranchSvg className="!transform-none" />}
                <ChevronRightIcon className="mr-2 block transition-transform" size={24} />
              </TreeView.BranchIndicator>
            )}
            <TreeView.BranchText className="flex items-center gap-2">
              <Checkbox checked={node.enabled} onChange={() => onToggle(node)} />
              {node.name}
            </TreeView.BranchText>
          </TreeView.BranchControl>
          <TreeView.BranchContent
            className={twJoin(
              "before:w-px before:bg-grey-300",
              depth > 0 ? "ml-10 before:left-[24.5px]" : "ml-6 before:left-[8.5px]",
              isLastChild || !node.children.some((x) => !x.isFinal) ? "before:hidden" : "before:absolute",
              !isLastChild && "before:h-[calc(100%-6rem)]",
            )}
          >
            <TreeView.BranchIndentGuide />
            {node.children.map((child, index) => (
              <TreeNode
                key={child.id}
                node={child}
                indexPath={[...indexPath, index]}
                depth={depth + 1}
                onEdit={onEdit}
                onToggle={onToggle}
                isLastChild={!!node.children && index === node.children.length - 1}
              />
            ))}
          </TreeView.BranchContent>
        </TreeView.Branch>
      )}
      {node.isFinal && (
        <TreeView.Item className="ml-5 py-3">
          <TreeView.ItemText className="-ml-5 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <BranchSvg className="mr-4" />
              <Checkbox checked={node.enabled} onChange={() => onToggle(node)} />
              {node.name}
            </div>
            <div className="ml-20 flex flex-wrap items-center gap-2 @xl:ml-2 @2xl:gap-4">
              {node.externalCode && (
                <span>{t("page.ticket-category-tree.external-code", { code: node.externalCode })}</span>
              )}
              <Select
                items={["inside", "outside", "insideOrOutside"] as const}
                selected={node.locationType}
                keySelector={(x) => x}
                renderOption={(x) => {
                  switch (x) {
                    case "inside":
                      return t("page.ticket-category-tree.location-type.inside");
                    case "outside":
                      return t("page.ticket-category-tree.location-type.outside");
                    case "insideOrOutside":
                      return t("page.ticket-category-tree.location-type.inside-or-outside");
                  }
                }}
                onChange={(locationType) => onEdit({ node, locationType })}
                placement="bottom-right"
              />
            </div>
          </TreeView.ItemText>
        </TreeView.Item>
      )}
    </TreeView.NodeProvider>
  );
}

function BranchSvg({ className }: { className?: string }) {
  return (
    <svg
      className={twJoin("relative -left-4 -top-3 -mr-4 text-grey-300", className)}
      width="25"
      height="27"
      viewBox="0 0 25 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M1 0L1 18C1 22.4183 4.58172 26 9 26V26L25 26" stroke="currentColor" />
    </svg>
  );
}
